import React, { useEffect, useState } from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CURRENCIES = ["usd", "eur", "gbp", "inr", "aed", "sgd", "aud", "cad"];

export default function GeneratePaymentLink({ open, onOpenChange, onCreated }) {
  const [creating, setCreating] = useState(false);
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestId, setRequestId] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [service, setService] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [desc, setDesc] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/catalog").then((r) => {
      const names = [...(r.data.countries || []).map((c) => c.name), "Company Formation", "Bank Account Opening", "Annual Compliance", "Consultation"];
      setServices([...new Set(names.filter(Boolean))]);
    }).catch(() => {});
    api.get("/admin/requests").then((r) => setRequests(r.data.requests || [])).catch(() => {});
  }, [open]);

  const pickRequest = (rid) => {
    setRequestId(rid);
    const r = requests.find((x) => x.id === rid);
    if (!r) return;
    if (r.client_email) setEmail(r.client_email);
    if (r.client_name) setName(r.client_name);
    if (r.package_name) setService(r.package_name);
    if (r.amount) setAmount(r.amount);
    if (r.currency) setCurrency(String(r.currency).toLowerCase());
  };

  const reset = () => {
    setRequestId(""); setEmail(""); setName(""); setService("");
    setAmount(""); setCurrency("usd"); setDesc("");
  };

  const generate = async () => {
    if (!service.trim()) return toast.error("Enter a service name.");
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount.");
    if (!email.includes("@")) return toast.error("Enter the client's email.");
    setCreating(true);
    try {
      const r = await api.post("/admin/payment-links", {
        request_id: requestId || null,
        client_email: email,
        client_name: name || null,
        service_name: service,
        amount: Number(amount),
        currency,
        description: desc,
      });
      const url = r.data?.url;
      toast.success("Payment link generated.", url ? { action: { label: "Copy", onClick: () => navigator.clipboard?.writeText(url) } } : undefined);
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create link.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="generate-link-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Generate payment link</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <select value={requestId} onChange={(e) => pickRequest(e.target.value)} className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" data-testid="gl-request">
            <option value="">Standalone payment (no linked request)</option>
            {requests.map((r) => (
              <option key={r.id} value={r.id}>{r.package_name || "Service"} · {r.client_name || r.client_email || r.company_name || r.id}</option>
            ))}
          </select>
          <div className="grid sm:grid-cols-2 gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Client email" className="h-9 bg-background border-border text-sm" data-testid="gl-email" />
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Client name (optional)" className="h-9 bg-background border-border text-sm" data-testid="gl-name" />
          </div>
          <Input list="gl-services" value={service} onChange={(e) => setService(e.target.value)} placeholder="Service name" className="h-9 bg-background border-border text-sm" data-testid="gl-service" />
          <datalist id="gl-services">{services.map((s) => <option key={s} value={s} />)}</datalist>
          <div className="grid sm:grid-cols-2 gap-2">
            <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="h-9 bg-background border-border text-sm" data-testid="gl-amount" />
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="gl-currency">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
            </select>
          </div>
          <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Notes / description for the client (optional)" className="h-9 bg-background border-border text-sm" data-testid="gl-desc" />
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm border-slate-300 bg-card h-9" data-testid="gl-cancel">Cancel</Button>
            <Button size="sm" disabled={creating} onClick={generate} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-9" data-testid="gl-submit">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-1" /> Generate link</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
